import { type ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';

type ActionVariant = 'default' | 'outline' | 'ghost' | 'secondary' | 'emerald';

interface EmptyStateAction {
  label: string;
  /** React-router link target; mutually exclusive with onClick */
  to?: string;
  /** Click handler (e.g. for opening a modal); mutually exclusive with `to` */
  onClick?: () => void;
  variant?: ActionVariant;
  icon?: ReactNode;
}

interface EmptyStateCardProps {
  title: string;
  description?: string;
  message: string;
  icon?: ReactNode;
  action?: EmptyStateAction;
  /** Optional secondary action (e.g. "Clear filters" next to "Add activity") */
  secondaryAction?: EmptyStateAction;
}

export const EmptyStateCard = ({
  title,
  description,
  message,
  icon,
  action,
  secondaryAction,
}: EmptyStateCardProps) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>
        <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-12 tw:text-muted-foreground'>
          {icon ? <div className='tw:mb-3 tw:opacity-50'>{icon}</div> : null}
          <p className='tw:text-sm tw:text-center tw:max-w-md tw:mb-0'>{message}</p>
          {(action || secondaryAction) ? (
            <div className='tw:flex tw:gap-2 tw:mt-4'>
              {action ? <ActionButton action={action} /> : null}
              {secondaryAction ? <ActionButton action={secondaryAction} defaultVariant='outline' /> : null}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

interface ActionButtonProps {
  action: EmptyStateAction;
  defaultVariant?: ActionVariant;
}

// Kept as a component (not a helper fn) so we can use `useHistory` for navigation.
// Rationale: avoid the `<Button asChild><Link /></Button>` pattern — nesting an
// anchor inside a Button bleeds link styles into the button and breaks the look.
const ActionButton = ({ action, defaultVariant = 'default' }: ActionButtonProps) => {
  const history = useHistory();
  const variant = action.variant ?? defaultVariant;
  const handleClick = action.to ? () => history.push(action.to!) : action.onClick;

  return (
    <Button variant={variant} onClick={handleClick}>
      {action.icon ? <span className='tw:mr-2 tw:inline-flex'>{action.icon}</span> : null}
      {action.label}
    </Button>
  );
};
